import React, { useState } from "react";
import Chart from "react-apexcharts";
import { FaDownload, FaFilter } from "react-icons/fa6";
import * as XLSX from "xlsx";
import MonthPicker from "../../../components/reusable-monthPicker/MonthPicker";
import Loader from "../../../components/reusable-loader/Loader";
import Select from "../../../components/reusable-select/Select";
import { showSnackbar } from "../../../redux/slices/snackbarSlice";
import { useDispatch } from "react-redux";

const generateBrownShades = (count: number): string[] => {
  const baseHue = 30;
  const saturation = 45;
  return Array.from({ length: count }, (_, i) => {
    const lightness = 30 + i * (40 / Math.max(count, 1));
    return `hsl(${baseHue}, ${saturation}%, ${lightness}%)`;
  });
};

const DownloadIcon = FaDownload as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

const FilterIcon = FaFilter as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

interface CategoryData {
  categories: string[];
  values: number[];
  total: number;
  title: string;
}

interface Props {
  practiceTest: CategoryData | null;
  learningCourse: CategoryData | null;
  setDateRange: (range: { startDate: Date; endDate: Date }) => void;
  loading: boolean;
  libraries: { label: string; value: string }[];
  selectedLibrary: string;
  onLibraryChange: (value: string) => void;
}

const PopularCategoriesChart: React.FC<Props> = ({
  practiceTest,
  learningCourse,
  setDateRange,
  loading,
  libraries,
  selectedLibrary,
  onLibraryChange,
}) => {
  const dispatch = useDispatch();
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  const handleMonthChange = (range: { startDate: Date; endDate: Date }) => {
    setSelectedMonth(range.startDate);
    setDateRange(range);
  };

  // ✅ Sanitize categories & values (remove nulls, replace with defaults)
  const sanitizeData = (data: CategoryData | null): CategoryData | null => {
    if (!data) return null;

    const aggregatedMap = new Map<string, number>();

    data.categories.forEach((cat, idx) => {
      const label = cat ?? "Unknown";
      const value = data.values[idx] ?? 0;

      if (label && value > 0) {
        aggregatedMap.set(label, (aggregatedMap.get(label) || 0) + value);
      }
    });

    if (aggregatedMap.size === 0) return null;

    // Convert map to array and sort by descending value
    const sortedEntries = Array.from(aggregatedMap.entries()).sort(
      (a, b) => b[1] - a[1]
    );

    const safeCategories = sortedEntries.map(([label]) => label);
    const safeValues = sortedEntries.map(([_, value]) => value);

    return {
      ...data,
      categories: safeCategories,
      values: safeValues,
      total: safeValues.reduce((a, b) => a + b, 0),
    };
  };

  const safePractice = sanitizeData(practiceTest);
  const safeLearning = sanitizeData(learningCourse);

  const colors = generateBrownShades(
    Math.max(
      safePractice?.categories.length || 0,
      safeLearning?.categories.length || 0
    )
  );

  const baseDonutOptions: ApexCharts.ApexOptions = {
    chart: { type: "donut" },
    legend: { show: false, position: "right" },
    dataLabels: { enabled: false },
    states: {
      hover: { filter: { type: "none" } },
      active: { filter: { type: "none" } },
    },
    plotOptions: {
      pie: {
        customScale: 1.0,
        donut: {
          size: "55%",
          labels: {
            show: true,
            value: { fontSize: "22px", fontWeight: "bold" },
            total: {
              show: true,
              fontSize: "26px",
              fontWeight: "bold",
              formatter: (w) => {
                const sum: number = (w.globals.seriesTotals as number[]).reduce(
                  (a: number, b: number) => a + b,
                  0
                );
                return sum.toString();
              },
            },
          },
        },
      },
    },
    colors,
  };

  const handleDownload = () => {
    if (
      (!safePractice || safePractice.categories.length === 0) &&
      (!safeLearning || safeLearning.categories.length === 0)
    ) {
      dispatch(
        showSnackbar({
          message: "No data available to download.",
          type: "error",
        })
      );
      return;
    }

    const monthYear = new Date().toLocaleString("default", {
      month: "short",
      year: "numeric",
    });

    let sheetData: any[] = [];

    const selectedLibraryLabel =
      selectedLibrary === "all"
        ? "All Libraries"
        : libraries.find((lib) => lib.value === selectedLibrary)?.label ||
          selectedLibrary;

    sheetData.push([selectedLibraryLabel]);
    sheetData.push([]);

    if (safePractice && safePractice.categories.length > 0) {
      sheetData.push([safePractice.title]);
      sheetData.push(["S.No", "Category", "No of code requests"]);

      safePractice.categories.forEach((cat, idx) => {
        sheetData.push([idx + 1, cat, safePractice.values[idx]]);
      });

      sheetData.push([]);
    }

    if (safeLearning && safeLearning.categories.length > 0) {
      sheetData.push([safeLearning.title]);
      sheetData.push(["S.No", "Category", "No of code requests"]);

      safeLearning.categories.forEach((cat, idx) => {
        sheetData.push([idx + 1, cat, safeLearning.values[idx]]);
      });
    }

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Popular Categories");

    XLSX.writeFile(
      workbook,
      `${
        selectedLibrary === "all" ? "All" : selectedLibraryLabel
      }_PopularCategoriesReport_${monthYear}.xlsx`
    );
  };

  const renderChartWithLegend = (data: CategoryData) => {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
        <div style={{ width: 260 }}>
          <h4 style={{ textAlign: "center", marginBottom: "10px" }}>
            {data.title}
          </h4>
          <Chart
            options={{
              ...baseDonutOptions,
              labels: data.categories,
              plotOptions: {
                ...baseDonutOptions.plotOptions,
                pie: {
                  ...baseDonutOptions.plotOptions?.pie,
                  donut: {
                    ...baseDonutOptions.plotOptions?.pie?.donut,
                    labels: {
                      show: true,
                      total: {
                        show: true,
                        label: "",
                        formatter: () => data.total.toString(),
                      },
                    },
                  },
                },
              },
            }}
            series={data.values}
            type="donut"
            width={260}
            height={240}
          />
        </div>

        {/* Custom Legend */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {data.categories.map((cat, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: "14px",
                padding: "4px 8px",
                borderRadius: "12px",
                minWidth: "200px",
                gap: "6px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: colors[idx % colors.length],
                  }}
                ></span>
                {cat}
              </div>
              <span
                style={{
                  background: "#eee",
                  borderRadius: "50%",
                  padding: "2px 8px",
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                {data.values[idx]}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="chart-box">
      <div className="chart-title">
        Most Popular Categories
        <div className="reports-controls">
          <div className="reports-controls-filter-main">
            <FilterIcon style={{ color: "#555", fontSize: "16px" }} />
            <select
              value={selectedLibrary}
              onChange={(e) => {
                const value = e.target.value === "all" ? "all" : e.target.value;
                onLibraryChange(value);
              }}
              className="reports-controls-filter-select"
            >
              {libraries.map((lib) => (
                <option key={lib.value} value={lib.value}>
                  {lib.label}
                </option>
              ))}
            </select>
          </div>

          <MonthPicker value={selectedMonth} onChange={handleMonthChange} />
          <button className="reports-download" onClick={handleDownload}>
            <DownloadIcon /> Download
          </button>
        </div>
      </div>

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}
        >
          <Loader />
        </div>
      ) : !safePractice && !safeLearning ? (
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            color: "#666",
            fontSize: "16px",
          }}
        >
          No data to display.
        </div>
      ) : (
        <div style={{ display: "flex", gap: "60px", justifyContent: "center" }}>
          {!safePractice ? (
            <div
              style={{ textAlign: "center", padding: "2rem", color: "#666" }}
            >
              No data available for Practice Test
            </div>
          ) : (
            renderChartWithLegend(safePractice)
          )}

          {!safeLearning ? (
            <div
              style={{ textAlign: "center", padding: "2rem", color: "#666" }}
            >
              No data available for Learning Course
            </div>
          ) : (
            renderChartWithLegend(safeLearning)
          )}
        </div>
      )}
    </div>
  );
};

export default PopularCategoriesChart;
